/**
 * Google AdSense integration service
 * Handles ad loading, error handling, and environment-specific configuration
 */

import { conditionalLog, conditionalWarn, conditionalError } from './utils/logger';

export interface AdConfig {
  pubId: string;
  adSlot?: string;
  adType?: 'treasure-card' | 'sidebar';
  format?: 'auto' | 'rectangle' | 'vertical' | 'horizontal';
  responsive?: boolean;
  className?: string;
}

export interface AdPerformanceMetrics {
  impressions: number;
  clicks: number;
  errors: number;
  loadTime: number;
}

class GoogleAdsService {
  private isInitialized = false;
  private pubId: string | null = null;
  private metrics: AdPerformanceMetrics = {
    impressions: 0,
    clicks: 0,
    errors: 0,
    loadTime: 0
  };

  /**
   * Initialize Google AdSense with environment-specific pub ID
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Get environment-specific pub ID
      this.pubId = this.getPubId();
      
      if (!this.pubId) {
        conditionalWarn('Google Ads: No pub ID configured for current environment');
        return;
      }

      // Load AdSense script if not already loaded
      if (!document.querySelector('script[src*="adsbygoogle.js"]')) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${this.pubId}`;
        script.crossOrigin = 'anonymous';
        
        // Add error handling for script loading
        script.onerror = () => {
          conditionalError('Google Ads: Failed to load AdSense script');
          this.metrics.errors++;
        };

        document.head.appendChild(script);
      }

      this.isInitialized = true;
      conditionalLog('Google Ads: Initialized with pub ID:', this.pubId);
    } catch (error) {
      conditionalError('Google Ads: Initialization failed:', error);
      this.metrics.errors++;
    }
  }

  /**
   * Get pub ID from environment variable
   */
  private getPubId(): string | null {
    return import.meta.env.VITE_GOOGLE_ADS_PUB_ID || null;
  }

  /**
   * Get ad slot ID for specific ad type
   */
  private getAdSlot(adType: 'treasure-card' | 'sidebar'): string | null {
    switch (adType) {
      case 'treasure-card':
        return import.meta.env.VITE_GOOGLE_ADS_TREASURE_CARD_SLOT || null;
      case 'sidebar':
        return import.meta.env.VITE_GOOGLE_ADS_SIDEBAR_SLOT || null;
      default:
        return null;
    }
  }

  /**
   * Create an ad element (to be pushed to AdSense by the component after DOM insertion)
   */
  async displayAd(config: AdConfig): Promise<HTMLElement | null> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.pubId) {
      conditionalWarn('Google Ads: Cannot display ad - no pub ID configured');
      return null;
    }

    try {
      const startTime = performance.now();

      // Create ad container with unique ID to prevent duplicates
      const adContainer = document.createElement('ins');
      const uniqueId = `ad-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      adContainer.className = `adsbygoogle ${config.className || ''}`;
      adContainer.style.display = 'block';
      adContainer.setAttribute('data-ad-client', this.pubId);
      adContainer.setAttribute('data-ad-id', uniqueId);
      
      // Use slot ID from config or get from environment based on ad type
      const adSlot = config.adSlot || (config.adType ? this.getAdSlot(config.adType) : null);
      if (adSlot) {
        adContainer.setAttribute('data-ad-slot', adSlot);
      }
      
      if (config.format) {
        adContainer.setAttribute('data-ad-format', config.format);
      }
      
      if (config.responsive !== false) {
        adContainer.setAttribute('data-full-width-responsive', 'true');
      }

      // Return the container - the parent component will append it to DOM and then push to AdSense
      const loadTime = performance.now() - startTime;
      this.metrics.loadTime = loadTime;
      
      conditionalLog(`Google Ads: Ad element created successfully in ${loadTime.toFixed(2)}ms`);
      
      return adContainer;
    } catch (error) {
      conditionalError('Google Ads: Failed to display ad:', error);
      this.metrics.errors++;
      return null;
    }
  }

  /**
   * Track ad click
   */
  trackClick(): void {
    this.metrics.clicks++;
    conditionalLog('Google Ads: Click tracked');
  }

  /**
   * Get performance metrics
   */
  getMetrics(): AdPerformanceMetrics {
    return { ...this.metrics };
  }

  /**
   * Check if ads are available
   */
  isAvailable(): boolean {
    return this.isInitialized && !!this.pubId;
  }

  /**
   * Get current pub ID
   */
  getCurrentPubId(): string | null {
    return this.pubId;
  }

  /**
   * Reset metrics
   */
  resetMetrics(): void {
    this.metrics = {
      impressions: 0,
      clicks: 0,
      errors: 0,
      loadTime: 0
    };
  }

  /**
   * Get configured slot ID for ad type (for debugging)
   */
  getConfiguredSlot(adType: 'treasure-card' | 'sidebar'): string | null {
    return this.getAdSlot(adType);
  }

  /**
   * Track successful ad impression (called after successful push)
   */
  trackImpression(): void {
    this.metrics.impressions++;
  }
}

// Export singleton instance
export const googleAdsService = new GoogleAdsService();

// Export types (already exported as interfaces above)